import { FORMAT_VERSION, HEADER_SIZE, MAGIC_0, MAGIC_1 } from "./format"
import { mortonDecode } from "./morton"

interface Header {
    depth: number
    nibbleCount: number
    pointCount: number
}

function parseHeader(data: Uint8Array): Header {
    if (data[0] !== MAGIC_0 || data[1] !== MAGIC_1) {
        throw new Error(`invalid magic: expected 0x${MAGIC_0.toString(16)}${MAGIC_1.toString(16)}`)
    }
    if (data[3] !== FORMAT_VERSION) {
        throw new Error(`unsupported version ${data[3]}, expected ${FORMAT_VERSION}`)
    }
    const depth = data[2]
    if (depth < 1 || depth > 26) throw new RangeError(`stored depth ${depth} out of range 1-26`)
    const nibbleCount = data[4] | (data[5] << 8) | (data[6] << 16) | (data[7] << 24)
    if (data.length < HEADER_SIZE + Math.ceil(nibbleCount / 2)) {
        throw new RangeError(
            `buffer truncated: expected ${HEADER_SIZE + Math.ceil(nibbleCount / 2)} bytes, got ${data.length}`
        )
    }
    const pointCount = data[8] | (data[9] << 8) | (data[10] << 16) | (data[11] << 24)
    return { depth, nibbleCount, pointCount }
}

type Frame = { level: number; path: bigint; bitmap: number; nextChild: number }

function walkTrie(
    data: Uint8Array,
    depth: number,
    nibbleCount: number,
    onLeaf: (path: bigint) => void
): void {
    if (nibbleCount === 0) return
    let ni = 0
    const readNibble = (): number => {
        const byte = data[HEADER_SIZE + (ni >> 1)]
        return (ni++ & 1) === 0 ? (byte >> 4) & 0xf : byte & 0xf
    }
    const stack: Frame[] = [{ level: 0, path: 0n, bitmap: readNibble(), nextChild: 0 }]
    while (stack.length > 0) {
        const top = stack[stack.length - 1]
        while (top.nextChild < 4 && !(top.bitmap & (1 << top.nextChild))) top.nextChild++
        if (top.nextChild >= 4) {
            stack.pop()
            continue
        }
        const child = top.nextChild++
        const childPath = top.path | (BigInt(child) << BigInt(2 * (depth - 1 - top.level)))
        if (top.level + 1 >= depth) {
            onLeaf(childPath)
        } else {
            stack.push({
                level: top.level + 1,
                path: childPath,
                bitmap: readNibble(),
                nextChild: 0,
            })
        }
    }
}

export function decodeEach(data: Uint8Array, callback: (lat: number, lon: number) => void): void {
    const { depth, nibbleCount } = parseHeader(data)
    walkTrie(data, depth, nibbleCount, (path) => {
        const [lat, lon] = mortonDecode(path, depth)
        callback(lat, lon)
    })
}

export function decode(data: Uint8Array): Array<[number, number]> {
    const { depth, nibbleCount, pointCount } = parseHeader(data)
    const result = new Array<[number, number]>(pointCount)
    let i = 0
    walkTrie(data, depth, nibbleCount, (path) => {
        result[i++] = mortonDecode(path, depth)
    })
    return result
}

export function* decodeStream(data: Uint8Array): Generator<[number, number]> {
    const { depth, nibbleCount } = parseHeader(data)
    if (nibbleCount === 0) return
    let ni = 0
    const readNibble = (): number => {
        const byte = data[HEADER_SIZE + (ni >> 1)]
        return (ni++ & 1) === 0 ? (byte >> 4) & 0xf : byte & 0xf
    }
    const stack: Frame[] = [{ level: 0, path: 0n, bitmap: readNibble(), nextChild: 0 }]
    while (stack.length > 0) {
        const top = stack[stack.length - 1]
        while (top.nextChild < 4 && !(top.bitmap & (1 << top.nextChild))) top.nextChild++
        if (top.nextChild >= 4) {
            stack.pop()
            continue
        }
        const child = top.nextChild++
        const childPath = top.path | (BigInt(child) << BigInt(2 * (depth - 1 - top.level)))
        if (top.level + 1 >= depth) {
            yield mortonDecode(childPath, depth)
        } else {
            stack.push({
                level: top.level + 1,
                path: childPath,
                bitmap: readNibble(),
                nextChild: 0,
            })
        }
    }
}
